/**
 * Materials are data: adding one here makes it exist everywhere (simulation, palette, brushes).
 * Ids and names follow the material table of the GDD (section 11).
 */

export const STATE_STATIC = 0;
export const STATE_POWDER = 1;
export const STATE_LIQUID = 2;
export const STATE_GAS = 3;

/** Default sideways flow of a liquid per tick; also the most any liquid may flow (the wake rules rely on it). */
export const DEFAULT_SPREAD = 4;
export type MaterialState = typeof STATE_STATIC | typeof STATE_POWDER | typeof STATE_LIQUID | typeof STATE_GAS;

export const FLAG_FLAMMABLE = 1 << 0;
/** Explodes on contact with fire or lava (black powder). */
export const FLAG_EXPLOSIVE = 1 << 1;
/** A gas that stays where it is, its lifetime stopped, while it touches something flammable: a flame clings to its fuel. */
export const FLAG_CLINGS = 1 << 2;
/** Engine-only material: hidden from palettes and brushes. */
export const FLAG_INTERNAL = 1 << 7;

export interface Material {
  /** Display name (French, as in the GDD). */
  readonly name: string;
  readonly state: MaterialState;
  /** 0-255. A moving material sinks through liquids and gases of lower density. */
  readonly density: number;
  readonly flags: number;
  /** 0xRRGGBB, also the colour of the material in map images. */
  readonly color: number;
  /**
   * Development wave that brings the material's behaviour (GDD section 11, water moved to the MVP).
   * 0 = MVP. Tools only offer the waves already built.
   */
  readonly wave: number;
  /** What a destroy tool (the pistol) turns this material into. */
  readonly crumblesInto?: number;
  /** Liquids: cells it may flow sideways per tick (LIQUID_SPREAD if absent). Lava is slow: 1. */
  readonly spread?: number;
  /** Gases: lifetime in ticks, drawn in [min, max] when the cell appears; then it becomes `decaysInto`. */
  readonly life?: readonly [number, number];
  readonly decaysInto?: number;
  /** Explosions it takes to destroy a cell (1 if absent). Obsidian: 3. */
  readonly blastHits?: number;
  /** What an explosion turns it into instead of blowing it away (water boils into steam). */
  readonly boilsInto?: number;
  /** Powders: false if it never slides diagonally (gravel). */
  readonly slides?: boolean;
}

// Ids are indices into MATERIALS; the grid stores them as bytes.
export const EMPTY = 0;
export const ROCK = 1;
export const SAND = 2;
export const WATER = 3;
export const LAVA = 4;
export const WOOD = 5;
export const STEAM = 6;
export const OBSIDIAN = 7;
export const FIRE = 8;
export const BLACK_POWDER = 9;
export const OIL = 10;
export const ACID = 11;
export const ICE = 12;
export const GRAVEL = 13;
/** Stamped transiently by game entities during step() so powders pile on them; never persisted. */
export const ENTITY = 14;

/** Highest wave whose materials behave as designed. Raise it when a wave is built. */
export const BUILT_WAVE = 4;

export const MATERIALS: readonly Material[] = [
  { name: 'Empty', state: STATE_GAS, density: 0, flags: 0, color: 0x1a1c24, wave: 0 },
  { name: 'Rock', state: STATE_STATIC, density: 200, flags: 0, color: 0x504860, wave: 0, crumblesInto: SAND },
  { name: 'Sand', state: STATE_POWDER, density: 150, flags: 0, color: 0xd6b85a, wave: 0 },
  { name: 'Water', state: STATE_LIQUID, density: 100, flags: 0, color: 0x2f6fd0, wave: 0, boilsInto: STEAM },
  { name: 'Lava', state: STATE_LIQUID, density: 220, flags: 0, color: 0xff5a1f, wave: 1, spread: 1 },
  { name: 'Wood', state: STATE_STATIC, density: 120, flags: FLAG_FLAMMABLE, color: 0x7a4a26, wave: 1 },
  // GDD: steam turns back into water after 80 to 200 ticks (rain).
  { name: 'Steam', state: STATE_GAS, density: 5, flags: 0, color: 0xc8d4dc, wave: 1, life: [80, 200], decaysInto: WATER },
  { name: 'Obsidian', state: STATE_STATIC, density: 250, flags: 0, color: 0x2a1f3d, wave: 1, blastHits: 3 },
  // GDD: fire rises, spreads to what burns and goes out after 20 to 50 ticks (counted once it has nothing left to burn).
  { name: 'Fire', state: STATE_GAS, density: 3, flags: FLAG_CLINGS, color: 0xffb02e, wave: 2, life: [20, 50], decaysInto: EMPTY },
  { name: 'Black powder', state: STATE_POWDER, density: 140, flags: FLAG_FLAMMABLE | FLAG_EXPLOSIVE, color: 0x26262b, wave: 2 },
  { name: 'Oil', state: STATE_LIQUID, density: 80, flags: FLAG_FLAMMABLE, color: 0x5a4a1a, wave: 2 },
  // Eats rock, sand, gravel and wood (reactions); heavier than water, so it sinks under it.
  { name: 'Acid', state: STATE_LIQUID, density: 110, flags: 0, color: 0x8fe03a, wave: 3, spread: 3 },
  { name: 'Ice', state: STATE_STATIC, density: 90, flags: 0, color: 0xa8e4f4, wave: 4 },
  // GDD: falls without sliding diagonally, so it makes steep columns.
  { name: 'Gravel', state: STATE_POWDER, density: 170, flags: 0, color: 0x8c8a84, wave: 4, slides: false },
  { name: 'Entity', state: STATE_STATIC, density: 255, flags: FLAG_INTERNAL, color: 0xff00ff, wave: 0 },
];

export const NO_CRUMBLE = 255;
export const NO_DECAY = 255;

/** Flat lookup tables built once from the material list, for use in per-cell loops. */
export interface MaterialTables {
  readonly count: number;
  readonly state: Uint8Array;
  readonly density: Uint8Array;
  readonly flags: Uint8Array;
  /** Target id, or NO_CRUMBLE. */
  readonly crumble: Uint8Array;
  /** displace[(mover << 8) | target] = 1 if a moving `mover` may swap with `target`. */
  readonly displace: Uint8Array;
  /** Liquids: cells per tick sideways. */
  readonly spread: Uint8Array;
  /** Gases with a lifetime: minimum, and number of possible values (0 = no lifetime). */
  readonly lifeMin: Uint8Array;
  readonly lifeSpan: Uint8Array;
  /** Target id when the lifetime ends, or NO_DECAY. */
  readonly decay: Uint8Array;
  readonly blastHits: Uint8Array;
  /** Target id when an explosion boils the cell, or NO_DECAY. */
  readonly boil: Uint8Array;
  /** 1 for a powder that never slides diagonally. */
  readonly noSlide: Uint8Array;
}

export function compileMaterials(materials: readonly Material[]): MaterialTables {
  if (materials.length === 0 || materials.length > 255) throw new Error('between 1 and 255 materials are supported');
  if (materials[EMPTY].state !== STATE_GAS || materials[EMPTY].density !== 0) throw new Error('material 0 must be an empty gas of density 0');
  const count = materials.length;
  const colors = new Set<number>();
  for (const m of materials) {
    if ((m.flags & FLAG_INTERNAL) !== 0) continue;
    if (colors.has(m.color)) throw new Error(`${m.name}: colour already used (map images need one colour per material)`);
    colors.add(m.color);
  }
  const state = new Uint8Array(256);
  const density = new Uint8Array(256);
  const flags = new Uint8Array(256);
  const crumble = new Uint8Array(256).fill(NO_CRUMBLE);
  const spread = new Uint8Array(256).fill(DEFAULT_SPREAD);
  const lifeMin = new Uint8Array(256);
  const lifeSpan = new Uint8Array(256);
  const decay = new Uint8Array(256).fill(NO_DECAY);
  const blastHits = new Uint8Array(256).fill(1);
  const boil = new Uint8Array(256).fill(NO_DECAY);
  const noSlide = new Uint8Array(256);

  materials.forEach((m, id) => {
    if (m.density < 0 || m.density > 255) throw new Error(`${m.name}: density must be 0-255`);
    if (m.crumblesInto !== undefined && (m.crumblesInto < 0 || m.crumblesInto >= count)) throw new Error(`${m.name}: unknown crumblesInto`);
    state[id] = m.state;
    density[id] = m.density;
    flags[id] = m.flags;
    if (m.crumblesInto !== undefined) crumble[id] = m.crumblesInto;
    if (m.spread !== undefined) {
      if (!Number.isInteger(m.spread) || m.spread < 1 || m.spread > DEFAULT_SPREAD) throw new Error(`${m.name}: spread must be 1-${DEFAULT_SPREAD}`);
      spread[id] = m.spread;
    }
    if (m.life !== undefined) {
      const [lo, hi] = m.life;
      if (m.state !== STATE_GAS || !Number.isInteger(lo) || !Number.isInteger(hi) || lo < 1 || hi < lo || hi > 254) throw new Error(`${m.name}: life must be a gas lifetime in 1-254`);
      if (m.decaysInto === undefined || m.decaysInto < 0 || m.decaysInto >= count) throw new Error(`${m.name}: a lifetime needs a known decaysInto`);
      lifeMin[id] = lo;
      lifeSpan[id] = hi - lo + 1;
      decay[id] = m.decaysInto;
    }
    if (m.slides === false) noSlide[id] = 1;
    if (m.boilsInto !== undefined) {
      if (m.boilsInto < 0 || m.boilsInto >= count) throw new Error(`${m.name}: unknown boilsInto`);
      boil[id] = m.boilsInto;
    }
    if (m.blastHits !== undefined) {
      if (!Number.isInteger(m.blastHits) || m.blastHits < 1 || m.blastHits > 255) throw new Error(`${m.name}: blastHits must be 1-255`);
      blastHits[id] = m.blastHits;
    }
  });

  const displace = new Uint8Array(256 * 256);
  for (let a = 0; a < count; a++) {
    if (state[a] === STATE_STATIC) continue;
    for (let b = 0; b < count; b++) {
      const fluid = state[b] === STATE_LIQUID || state[b] === STATE_GAS;
      if (fluid && density[b] < density[a]) displace[(a << 8) | b] = 1;
    }
  }
  return { count, state, density, flags, crumble, displace, spread, lifeMin, lifeSpan, decay, blastHits, boil, noSlide };
}

/**
 * Material of a map-image pixel (0xRRGGBB): the material with exactly this colour, else the one with the
 * closest colour (image editors may blur edges). Engine-only materials are never returned.
 */
export function materialForColor(materials: readonly Material[], rgb: number): number {
  let best = 0;
  let bestDist = Infinity;
  for (let id = 0; id < materials.length; id++) {
    const m = materials[id];
    if ((m.flags & FLAG_INTERNAL) !== 0) continue;
    if (m.color === rgb) return id;
    const dr = ((m.color >> 16) & 0xff) - ((rgb >> 16) & 0xff);
    const dg = ((m.color >> 8) & 0xff) - ((rgb >> 8) & 0xff);
    const db = (m.color & 0xff) - (rgb & 0xff);
    const dist = dr * dr + dg * dg + db * db;
    if (dist < bestDist) { bestDist = dist; best = id; }
  }
  return best;
}
